// After a rotate, iOS keeps reporting a STALE window.orientation /
// viewport size for a short window — the orientationchange event fires before the
// new values settle. `settleAfterRotate(sample)` re-runs `sample` on every
// animation frame for a brief window, so dependent state (the notch side, the
// viewport size) converges as fast as the value settles — within a frame — instead
// of waiting on fixed timeouts.
//
// `sample` applies its own update and MUST be idempotent (it runs many times).
// Returns a cancel() to stop early (component unmount / singleton teardown).
//
// Frame-sampling strictly dominates the fixed 150/350/600ms delays this replaces:
// it samples at every one of those instants AND in between, so it can only catch
// the settle earlier, never later. (A direct 180° landscape flip is still never
// reported by iOS at all — no sampling rate helps there; that stays a documented
// limitation.)
// Exported so callers that schedule a one-shot AFTER the settle window
// (VideoJs._recompositeLandscapeControls) can derive their delay from this
// instead of hardcoding a magic number that drifts if this is retuned.
export const SETTLE_MS = 650;

export function settleAfterRotate(sample: () => void): () => void {
  if (
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function"
  ) {
    sample();
    return function () {
      /* no-op */
    };
  }
  sample(); // apply immediately, then keep re-checking until the values settle
  let startTs: number | null = null;
  let rafId = 0;
  const step = function (ts: number) {
    if (startTs === null) {
      startTs = ts;
    }
    sample();
    rafId = ts - startTs < SETTLE_MS ? window.requestAnimationFrame(step) : 0;
  };
  rafId = window.requestAnimationFrame(step);
  return function cancel() {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };
}
